import React from 'react';
import { Box, Typography, useMediaQuery } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import Grid2 from '@mui/material/Grid2';
import { formatCurrency } from '../../../utils';
import { COMPACT_THRESHOLD } from '../../../../config/statusDisplayResolver';

const CircleLayout = ({ stats, onStatClick, isMobile }) => (
    <Box sx={{ mb: isMobile ? 2 : 3 }}>
        <Grid2 container spacing={isMobile ? 1.5 : 2}>
            {stats.map((stat, index) => (
                <Grid2 size={{ xs: 4, md: 2 }} key={index}>
                    <Box sx={{ textAlign: 'center' }}>
                        <Box
                            onClick={() => onStatClick?.(stat)}
                            sx={{
                                width: '100%',
                                aspectRatio: '1/1',
                                borderRadius: '50%',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                backgroundColor: '#f5f5f5',
                                boxShadow: 'inset 0 1px 2px rgba(0, 0, 0, 0.04)',
                                maxWidth: isMobile ? '75px' : '90px',
                                margin: '0 auto',
                                cursor: onStatClick ? 'pointer' : 'default',
                                transition: 'transform 0.2s ease, box-shadow 0.2s ease',
                                '&:hover': onStatClick
                                    ? {
                                          transform: 'scale(1.05)',
                                          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                                      }
                                    : {},
                            }}
                        >
                            <Typography
                                component="div"
                                sx={{
                                    fontWeight: 500,
                                    color: '#0a0b0d',
                                    fontSize: { xs: '1.1rem', md: '1.35rem' },
                                }}
                            >
                                {stat.count}
                            </Typography>
                        </Box>
                        <Typography
                            component="div"
                            sx={{
                                mt: 1,
                                color: '#5b616e',
                                fontSize: { xs: '0.75rem', md: '0.8125rem' },
                                fontWeight: 400,
                                lineHeight: 1.2,
                            }}
                        >
                            {stat.label}
                        </Typography>
                    </Box>
                </Grid2>
            ))}
        </Grid2>
        <Box sx={{ mt: isMobile ? 2 : 3, borderBottom: '1px solid #f0f0f0' }} />
    </Box>
);

const BarDivider = ({ isMobile }) =>
    isMobile ? (
        <Box sx={{ height: '1px', backgroundColor: '#d4d4d4', mx: 2 }} />
    ) : (
        <Box sx={{ width: '1px', height: '32px', backgroundColor: '#d4d4d4', flexShrink: 0 }} />
    );

const BarItem = ({ value, label, onClick, isMobile }) => (
    <Box
        onClick={onClick}
        sx={{
            flex: isMobile ? undefined : 1,
            display: 'flex',
            alignItems: 'baseline',
            justifyContent: isMobile ? 'flex-start' : 'center',
            gap: 1,
            cursor: onClick ? 'pointer' : 'default',
            borderRadius: '8px',
            py: isMobile ? 1.25 : 0.75,
            mx: 0.5,
            px: isMobile ? 1.5 : 0,
            transition: 'background-color 0.15s ease',
            '&:hover': onClick ? { backgroundColor: 'rgba(0,0,0,0.04)' } : {},
        }}
    >
        <Typography
            component="span"
            sx={{
                fontWeight: 600,
                color: '#0a0b0d',
                fontSize: isMobile ? '1.1rem' : '1.35rem',
                lineHeight: 1,
            }}
        >
            {value}
        </Typography>
        <Typography
            component="span"
            sx={{
                color: '#5b616e',
                fontSize: isMobile ? '0.85rem' : '0.9rem',
                fontWeight: 400,
                lineHeight: 1,
            }}
        >
            {label}
        </Typography>
    </Box>
);

const BarLayout = ({ stats, deferredValue, onStatClick, isMobile }) => (
    <Box sx={{ mb: isMobile ? 2 : 3 }}>
        <Box
            sx={{
                display: 'flex',
                flexDirection: isMobile ? 'column' : 'row',
                alignItems: isMobile ? 'stretch' : 'center',
                backgroundColor: '#f5f5f5',
                borderRadius: '12px',
                py: isMobile ? 1 : 2.5,
                px: isMobile ? 1 : 3,
            }}
        >
            {stats.map((stat, index) => (
                <React.Fragment key={index}>
                    {index > 0 && <BarDivider isMobile={isMobile} />}
                    <BarItem
                        value={stat.count}
                        label={stat.label}
                        onClick={onStatClick ? () => onStatClick(stat) : undefined}
                        isMobile={isMobile}
                    />
                </React.Fragment>
            ))}
            {deferredValue != null && (
                <>
                    <BarDivider isMobile={isMobile} />
                    <BarItem
                        value={formatCurrency(deferredValue)}
                        label="Total Deferred"
                        isMobile={isMobile}
                    />
                </>
            )}
        </Box>
    </Box>
);

const StatsCards = ({ stats, onStatClick, deferredValue }) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    if (stats.length < COMPACT_THRESHOLD) {
        return (
            <BarLayout
                stats={stats}
                deferredValue={deferredValue}
                onStatClick={onStatClick}
                isMobile={isMobile}
            />
        );
    }

    return <CircleLayout stats={stats} onStatClick={onStatClick} isMobile={isMobile} />;
};

export default StatsCards;
